import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import styled from '@emotion/styled';
import { projects } from '../data/projects';

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(to bottom, #000000, #1a1a1a);
  color: white;
  padding: 1rem 0.5rem;
  
  @media (min-width: 768px) {
    padding: 2rem 1rem;
  }
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 0.5rem;
  
  @media (min-width: 768px) {
    padding: 0 1rem;
  }
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.6);
  text-decoration: none;
  margin-bottom: 2rem;
  font-size: 0.9rem;
  transition: color 0.2s ease;

  svg {
    margin-right: 0.5rem;
    transition: transform 0.2s ease;
  }

  &:hover {
    color: white;
    
    svg {
      transform: translateX(-4px);
    }
  }
`;

const ProjectContainer = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 1rem;
  overflow: hidden;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const ProjectHeader = styled.div`
  position: relative;
  padding: 1.5rem;
  
  @media (min-width: 768px) {
    padding: 3rem;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(to right, 
      rgba(255, 255, 255, 0.1),
      rgba(255, 255, 255, 0.2),
      rgba(255, 255, 255, 0.1)
    );
  }
`;

const ProjectImage = styled.div`
  position: relative;
  aspect-ratio: 16/9;
  
  @media (min-width: 768px) {
    aspect-ratio: 21/9;
  }
  
  overflow: hidden;
  border-radius: 0.5rem;
  margin-bottom: 1.5rem;
  
  @media (min-width: 768px) {
    margin-bottom: 2rem;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to bottom, transparent 60%, rgba(0, 0, 0, 0.4));
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ProjectTitle = styled.h1`
  font-size: 2rem;
  
  @media (min-width: 768px) {
    font-size: 3rem;
  }
  
  font-weight: 700;
  margin-bottom: 0.75rem;
  
  @media (min-width: 768px) {
    margin-bottom: 1rem;
  }
  
  background: linear-gradient(to right, #fff, #888);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const ProjectDate = styled.p`
  color: rgba(255, 255, 255, 0.6);
  font-size: 1rem;
`;

const ProjectContent = styled.div`
  padding: 1.5rem;
  
  @media (min-width: 768px) {
    padding: 3rem;
  }
`;

const ProjectDescription = styled.p`
  font-size: 1.1rem;
  
  @media (min-width: 768px) {
    font-size: 1.25rem;
  }
  
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 2rem;
  
  @media (min-width: 768px) {
    margin-bottom: 3rem;
  }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }
`;

const DetailCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border-radius: 0.75rem;
  padding: 1.5rem;
  
  @media (min-width: 768px) {
    padding: 2rem;
  }
  
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.2);
  }
`;

const DetailTitle = styled.h3`
  font-size: 1.25rem;
  
  @media (min-width: 768px) {
    font-size: 1.5rem;
  }
  
  font-weight: 600;
  margin-bottom: 0.75rem;
  
  @media (min-width: 768px) {
    margin-bottom: 1rem;
  }
  
  color: white;
`;

const DetailContent = styled.p`
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
`;

const SpotsContainer = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border-radius: 0.75rem;
  padding: 1.5rem;
  margin-bottom: 2.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  gap: 1.5rem;

  @media (max-width: 768px) {
    padding: 1.25rem;
    margin-bottom: 2rem;
    flex-direction: column;
    gap: 1rem;
  }
`;

const SpotsInfo = styled.div`
  flex: 1;
  text-align: left;
`;

const SpotsTitle = styled.h3`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 0.5rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const SpotsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.5rem;
  width: 200px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100%;
    gap: 0.375rem;
  }
`;

const SpotItem = styled.div<{ available?: boolean }>`
  aspect-ratio: 1;
  background: ${props => props.available ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.15)'};
  border-radius: 0.375rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  font-size: 0.875rem;
  color: ${props => props.available ? 'rgba(255, 255, 255, 0.5)' : 'rgba(255, 255, 255, 0.9)'};
  border: 1px solid ${props => props.available ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.2)'};
  transition: all 0.2s ease;

  ${props => props.available && `
    &:hover {
      background: rgba(255, 255, 255, 0.08);
      border-color: rgba(255, 255, 255, 0.15);
    }
  `}
`;

const SpotsStatus = styled.div`
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.125rem;
  font-weight: 500;
  margin-top: 0.25rem;

  span {
    color: rgba(255, 255, 255, 0.5);
    font-size: 0.875rem;
    margin-left: 0.5rem;
  }
`;

const ApplyButton = styled.a`
  display: inline-block;
  background: linear-gradient(to right, #fff, rgba(255, 255, 255, 0.8));
  color: #000;
  padding: 1rem 2rem;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 1.1rem;
  text-decoration: none;
  margin: 1rem 0 3rem;
  transition: all 0.3s ease;
  text-align: center;
  
  @media (max-width: 768px) {
    display: block;
    margin: 0.5rem 0 2rem;
    padding: 0.875rem 1.5rem;
    font-size: 1rem;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(255, 255, 255, 0.2);
  }
`;

const GallerySection = styled.div`
  margin-top: 2rem;
  
  @media (min-width: 768px) {
    margin-top: 3rem;
  }
`;


const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 1rem;
  
  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 1.5rem;
  }
`;

const GalleryImage = styled.div`
  position: relative;
  aspect-ratio: 3/4;
  
  @media (min-width: 768px) {
    aspect-ratio: 16/9;
  }
  
  overflow: hidden;
  border-radius: 0.5rem;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }
  
  &:hover img {
    transform: scale(1.05);
  }
`;

const OverviewSection = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border-radius: 0.75rem;
  padding: 1.5rem;
  margin-bottom: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.1);

  @media (min-width: 768px) {
    padding: 2rem;
    margin-bottom: 3rem;
  }
`;

const OverviewTitle = styled.h3`
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 1rem;
`;

const OverviewContent = styled.p`
  font-size: 1.125rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.9);
  
  @media (min-width: 768px) {
    font-size: 1.25rem;
  }
`;

const ProgramDetailsSection = styled.div`
  margin: 2rem 0 3rem;
`;

const ProgramDetailsTitle = styled.h3`
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 1.5rem;
`;

const DetailsList = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }
`;

const DetailsGroup = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border-radius: 0.75rem;
  padding: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const DetailsGroupTitle = styled.h4`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 1rem;
  font-weight: 500;
`;

const DetailItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.875rem;
  line-height: 1.5;
  margin-bottom: 0.5rem;

  &:last-child {
    margin-bottom: 0;
  }

  svg {
    flex-shrink: 0;
    margin-top: 0.25rem;
  }
`;

const BasicInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 1.5rem;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 1.5rem;
  }
`;

const InfoItem = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border-radius: 0.5rem;
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const InfoLabel = styled.div`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const InfoValue = styled.div`
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
`;

const ProjectDetail: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const project = projects.find(p => p.id === projectId);

  if (!project) {
    return (
      <PageContainer>
        <ContentContainer>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <h2 className="text-4xl font-bold mb-4">Project Not Found</h2>
            <BackLink to="/projects">
              Return to Projects
            </BackLink>
          </motion.div>
        </ContentContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ContentContainer>
        <BackLink to="/projects">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back to Projects
        </BackLink>

        <ProjectContainer
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <ProjectHeader>
            {project.coverImage && (
              <ProjectImage>
                <img src={project.coverImage} alt={project.title} />
              </ProjectImage>
            )}
            <ProjectTitle>{project.title}</ProjectTitle>
            <ProjectDate>{project.date}</ProjectDate>
          </ProjectHeader>

          <ProjectContent>
            <ProjectDescription>
              {project.description}
            </ProjectDescription>

            <OverviewSection>
              <OverviewTitle>Program Overview</OverviewTitle>
              <OverviewContent>{project.overview}</OverviewContent>
            </OverviewSection>

            <SpotsContainer>
              <SpotsInfo>
                <SpotsTitle>Residency Spots</SpotsTitle>
                <SpotsStatus>
                  {project.programDetails.spots.available} Available <span>of {project.programDetails.spots.total} total spots</span>
                </SpotsStatus>
              </SpotsInfo>
              <SpotsGrid>
                <SpotItem>1</SpotItem>
                <SpotItem available>2</SpotItem>
                <SpotItem available>3</SpotItem>
                <SpotItem available>4</SpotItem>
              </SpotsGrid>
            </SpotsContainer>

            <ProgramDetailsSection>
              <ProgramDetailsTitle>Program Details</ProgramDetailsTitle>
              
              <BasicInfo>
                <InfoItem>
                  <InfoLabel>Duration</InfoLabel>
                  <InfoValue>{project.programDetails.duration}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Program Dates</InfoLabel>
                  <InfoValue>{project.programDetails.dates}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Creative Grant</InfoLabel>
                  <InfoValue>10,000 THB</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Revenue Share</InfoLabel>
                  <InfoValue>50/50 Split</InfoValue>
                </InfoItem>
              </BasicInfo>

              <DetailsList>
                <DetailsGroup>
                  <DetailsGroupTitle>Facilities & Resources</DetailsGroupTitle>
                  {project.programDetails.facilities.map((facility, index) => (
                    <DetailItem key={index}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                      {facility}
                    </DetailItem>
                  ))}
                </DetailsGroup>
                <DetailsGroup>
                  <DetailsGroupTitle>Requirements</DetailsGroupTitle>
                  {project.programDetails.requirements.map((requirement, index) => (
                    <DetailItem key={index}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                      {requirement}
                    </DetailItem>
                  ))}
                </DetailsGroup>
              </DetailsList>
            </ProgramDetailsSection>

            <ApplyButton 
              href="mailto:sheena.miyake@gmail.com?subject=Artist Residency Program Application"
              target="_blank"
              rel="noopener noreferrer"
            >
              Apply for Residency
            </ApplyButton>

            <DetailGrid>
              {project.details.map((detail, index) => (
                <DetailCard key={index}>
                  <DetailTitle>{detail.title}</DetailTitle>
                  <DetailContent>{detail.content}</DetailContent>
                </DetailCard>
              ))}
            </DetailGrid>

            {project.gallery && (
              <GallerySection>
                <GalleryGrid>
                  {project.gallery.map((image, index) => (
                    <GalleryImage key={index}>
                      <img src={image} alt={`Gallery image ${index + 1}`} />
                    </GalleryImage>
                  ))}
                </GalleryGrid>
              </GallerySection>
            )}
          </ProjectContent>
        </ProjectContainer>
      </ContentContainer>
    </PageContainer>
  );
};

export default ProjectDetail; 